import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Shield, 
  Users, 
  Building2, 
  Clock, 
  CheckCircle, 
  XCircle,
  MoreVertical,
  UserCheck,
  UserX,
  Search,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  UserCog,
  Crown
} from 'lucide-react';

// Available roles with descriptions
const ROLES = [
  { value: 'admin', label: 'Admin', description: 'Full access to all features', color: 'bg-purple-100 text-purple-700' },
  { value: 'finance', label: 'Finance', description: 'Financial operations & approvals', color: 'bg-blue-100 text-blue-700' },
  { value: 'accountant', label: 'Accountant', description: 'Transactions & reports', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access', color: 'bg-gray-100 text-gray-700' },
];

const getRoleBadgeColor = (role) => {
  const roleConfig = ROLES.find(r => r.value === role);
  return roleConfig?.color || 'bg-gray-100 text-gray-700';
};

const PlatformAdmin = () => {
  const navigate = useNavigate();
  const { user, isPlatformAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  
  // Dialog states
  const [approvalDialog, setApprovalDialog] = useState({ open: false, user: null, action: null });
  const [rejectionReason, setRejectionReason] = useState('');
  const [roleDialog, setRoleDialog] = useState({ open: false, user: null });
  const [selectedRole, setSelectedRole] = useState('');

  useEffect(() => {
    // Wait for auth to be ready before checking admin status
    // isPlatformAdmin will be undefined/false while loading
    if (isPlatformAdmin === false && user) {
      toast.error('Access denied. Platform admin privileges required.');
      navigate('/dashboard');
      return;
    }
    
    if (isPlatformAdmin === true) {
      loadData();
    }
  }, [isPlatformAdmin, user, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, pendingRes, allUsersRes, companiesRes] = await Promise.all([
        api.get('/platform-admin/stats'),
        api.get('/platform-admin/pending-users'),
        api.get('/platform-admin/all-users'),
        api.get('/platform-admin/all-companies')
      ]);
      
      setStats(statsRes.data);
      setPendingUsers(pendingRes.data);
      setAllUsers(allUsersRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error('Error loading platform admin data:', error);
      toast.error('Failed to load platform data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId, approved, reason = null) => {
    try {
      await api.post('/platform-admin/approve-user', {
        user_id: userId,
        approved,
        rejection_reason: reason
      });
      
      toast.success(approved ? 'User approved successfully' : 'User rejected');
      setApprovalDialog({ open: false, user: null, action: null });
      setRejectionReason('');
      loadData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleToggleUserStatus = async (userId) => {
    try {
      await api.post(`/platform-admin/toggle-user-status/${userId}`);
      toast.success('User status updated');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle user status');
    }
  };

  const handleUpdateRole = async () => {
    if (!roleDialog.user || !selectedRole) return;
    
    try {
      await api.post('/platform-admin/update-role', {
        user_id: roleDialog.user.id,
        role: selectedRole
      });
      toast.success(`Role updated to ${selectedRole}`);
      setRoleDialog({ open: false, user: null });
      setSelectedRole('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleTogglePlatformAdmin = async (userId, makePlatformAdmin) => {
    try {
      await api.post('/platform-admin/toggle-platform-admin', {
        user_id: userId,
        is_platform_admin: makePlatformAdmin
      });
      toast.success(makePlatformAdmin ? 'User promoted to Platform Admin' : 'Platform Admin access removed');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update platform admin status');
    }
  };

  const openRoleDialog = (u) => {
    setRoleDialog({ open: true, user: u });
    setSelectedRole(u.role);
  };

  const filteredUsers = allUsers.filter(u => 
    u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.company_name && u.company_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const filteredCompanies = companies.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="animate-fade-in p-6">
        <div className="flex items-center gap-3 mb-8">
          <Skeleton className="h-8 w-8 rounded-lg" />
          <Skeleton className="h-10 w-64" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-96 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="platform-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <Shield size={24} />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
              Platform Administration
            </h1>
            <p className="text-[hsl(var(--muted-ink))] text-sm">
              Manage users, companies, and platform settings
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadData}
          className="gap-2"
        >
          <RefreshCw size={16} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users size={18} className="text-blue-600" />
              <span className="text-sm text-blue-700 font-medium">Total Users</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">{stats.total_users || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={18} className="text-emerald-600" />
              <span className="text-sm text-emerald-700 font-medium">Active Users</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700">{stats.active_users || 0}</p>
          </CardContent>
        </Card>
        
        <Card className={`${stats.pending_approval > 0 ? 'bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200' : 'bg-gradient-to-br from-slate-50 to-slate-100/50 border-slate-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={18} className={stats.pending_approval > 0 ? 'text-amber-600' : 'text-slate-600'} />
              <span className={`text-sm font-medium ${stats.pending_approval > 0 ? 'text-amber-700' : 'text-slate-700'}`}>Pending Approval</span>
            </div>
            <p className={`text-2xl font-bold ${stats.pending_approval > 0 ? 'text-amber-700' : 'text-slate-700'}`}>
              {stats.pending_approval || 0}
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Building2 size={18} className="text-purple-600" />
              <span className="text-sm text-purple-700 font-medium">Companies</span>
            </div>
            <p className="text-2xl font-bold text-purple-700">{stats.total_companies || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Pending Approval Alert */}
      {pendingUsers.length > 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-full bg-amber-100">
              <AlertTriangle size={20} className="text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-amber-800">Users Awaiting Approval</p>
              <p className="text-sm text-amber-700">
                {pendingUsers.length} user{pendingUsers.length !== 1 ? 's' : ''} need{pendingUsers.length === 1 ? 's' : ''} your approval before they can access the platform
              </p>
            </div>
            <Button 
              size="sm" 
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={() => setActiveTab('pending')}
            >
              Review Now
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <TabsList>
            <TabsTrigger value="pending" className="gap-2">
              <Clock size={16} />
              Pending ({pendingUsers.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <Users size={16} />
              All Users
            </TabsTrigger>
            <TabsTrigger value="companies" className="gap-2">
              <Building2 size={16} />
              Companies
            </TabsTrigger>
          </TabsList>
          
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-ink))]" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
        </div>

        {/* Pending Users Tab */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Pending User Approvals</CardTitle>
              <CardDescription>
                Review and approve or reject new user registrations
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pendingUsers.length === 0 ? (
                <div className="text-center py-12 text-[hsl(var(--muted-ink))]">
                  <CheckCircle size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="font-medium">No pending approvals</p>
                  <p className="text-sm">All user registrations have been processed</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Registered</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingUsers.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{u.name}</p>
                            <p className="text-sm text-[hsl(var(--muted-ink))]">{u.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{u.company_name || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{u.role}</Badge>
                        </TableCell>
                        <TableCell className="text-[hsl(var(--muted-ink))]">
                          {u.created_at ? formatDate(u.created_at) : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1"
                              onClick={() => setApprovalDialog({ open: true, user: u, action: 'approve' })}
                            >
                              <UserCheck size={14} />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-200 text-red-600 hover:bg-red-50 gap-1"
                              onClick={() => setApprovalDialog({ open: true, user: u, action: 'reject' })}
                            >
                              <UserX size={14} />
                              Reject
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>All Platform Users</CardTitle>
              <CardDescription>
                View and manage all users across the platform - change roles, manage permissions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {u.is_platform_admin && (
                            <Crown size={14} className="text-amber-500" title="Platform Admin" />
                          )}
                          <div>
                            <p className="font-medium">{u.name}</p>
                            <p className="text-sm text-[hsl(var(--muted-ink))]">{u.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{u.company_name || '-'}</TableCell>
                      <TableCell>
                        <Badge className={`${getRoleBadgeColor(u.role)} hover:opacity-90 cursor-pointer`}
                               onClick={() => openRoleDialog(u)}>
                          {u.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {u.is_active ? (
                          u.is_approved ? (
                            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                              Active
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                              Pending
                            </Badge>
                          )
                        ) : (
                          <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-[hsl(var(--muted-ink))]">
                        {u.last_login ? formatDate(u.last_login) : 'Never'}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuLabel>Manage User</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            
                            {/* Role Management */}
                            <DropdownMenuItem onClick={() => openRoleDialog(u)}>
                              <UserCog size={14} className="mr-2" />
                              Change Role
                            </DropdownMenuItem>
                            
                            {/* Platform Admin Toggle */}
                            {u.id !== user?.id && (
                              <DropdownMenuItem 
                                onClick={() => handleTogglePlatformAdmin(u.id, !u.is_platform_admin)}
                                className={u.is_platform_admin ? 'text-amber-600' : 'text-purple-600'}
                              >
                                {u.is_platform_admin ? (
                                  <>
                                    <Shield size={14} className="mr-2" />
                                    Remove Platform Admin
                                  </>
                                ) : (
                                  <>
                                    <Crown size={14} className="mr-2" />
                                    Make Platform Admin
                                  </>
                                )}
                              </DropdownMenuItem>
                            )}
                            
                            <DropdownMenuSeparator />
                            
                            {/* Approval for pending users */}
                            {!u.is_approved && u.is_active && (
                              <DropdownMenuItem 
                                onClick={() => handleApproveUser(u.id, true)}
                                className="text-emerald-600"
                              >
                                <UserCheck size={14} className="mr-2" />
                                Approve
                              </DropdownMenuItem>
                            )}
                            
                            {/* Activate/Deactivate */}
                            <DropdownMenuItem 
                              onClick={() => handleToggleUserStatus(u.id)}
                              className={u.is_active ? 'text-red-600' : 'text-emerald-600'}
                              disabled={u.id === user?.id}
                            >
                              {u.is_active ? (
                                <>
                                  <XCircle size={14} className="mr-2" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <CheckCircle size={14} className="mr-2" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Companies Tab */}
        <TabsContent value="companies">
          <Card>
            <CardHeader>
              <CardTitle>All Companies</CardTitle>
              <CardDescription>
                View all companies registered on the platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company Name</TableHead>
                    <TableHead>Business Type</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Users</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCompanies.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {c.business_type || 'services'}
                        </Badge>
                      </TableCell>
                      <TableCell>{c.currency || 'USD'}</TableCell>
                      <TableCell>
                        <span className="text-[hsl(var(--muted-ink))]">
                          {c.active_users}/{c.user_count} active
                        </span>
                      </TableCell>
                      <TableCell className="text-[hsl(var(--muted-ink))]">
                        {c.created_at ? formatDate(c.created_at) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Approval Dialog */}
      <AlertDialog 
        open={approvalDialog.open && approvalDialog.action === 'approve'} 
        onOpenChange={(open) => !open && setApprovalDialog({ open: false, user: null, action: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to approve <strong>{approvalDialog.user?.name}</strong> ({approvalDialog.user?.email})?
              They will receive an email notification and be able to access the platform.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-emerald-500 hover:bg-emerald-600"
              onClick={() => handleApproveUser(approvalDialog.user?.id, true)}
            >
              Approve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rejection Dialog */}
      <Dialog 
        open={approvalDialog.open && approvalDialog.action === 'reject'} 
        onOpenChange={(open) => !open && setApprovalDialog({ open: false, user: null, action: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject User</DialogTitle>
            <DialogDescription>
              Are you sure you want to reject <strong>{approvalDialog.user?.name}</strong> ({approvalDialog.user?.email})?
              They will be notified via email.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">
              Rejection Reason (optional)
            </label>
            <Input
              placeholder="Enter a reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialog({ open: false, user: null, action: null })}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleApproveUser(approvalDialog.user?.id, false, rejectionReason)}
            >
              Reject User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Management Dialog */}
      <Dialog 
        open={roleDialog.open} 
        onOpenChange={(open) => !open && setRoleDialog({ open: false, user: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Update role for <strong>{roleDialog.user?.name}</strong> ({roleDialog.user?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Select Role</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex flex-col">
                        <span className="font-medium">{role.label}</span>
                        <span className="text-xs text-[hsl(var(--muted-ink))]">{role.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="bg-[hsl(var(--surface-2))] p-3 rounded-lg">
              <p className="text-sm font-medium mb-2">Role Permissions:</p>
              <ul className="text-xs text-[hsl(var(--muted-ink))] space-y-1">
                {selectedRole === 'admin' && (
                  <>
                    <li>• Full access to all features</li>
                    <li>• Manage team members</li>
                    <li>• Configure company settings</li>
                    <li>• Approve transactions</li>
                  </>
                )}
                {selectedRole === 'finance' && (
                  <>
                    <li>• Create and approve transactions</li>
                    <li>• Manage receivables & payables</li>
                    <li>• View all financial reports</li>
                    <li>• No team management access</li>
                  </>
                )}
                {selectedRole === 'accountant' && (
                  <>
                    <li>• Create transactions (no approval)</li>
                    <li>• Manage journal entries</li>
                    <li>• View audit logs</li>
                    <li>• Generate reports</li>
                  </>
                )}
                {selectedRole === 'viewer' && (
                  <>
                    <li>• Read-only access to all data</li>
                    <li>• View reports and dashboards</li>
                    <li>• Cannot create or modify records</li>
                  </>
                )}
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateRole}
              disabled={!selectedRole || selectedRole === roleDialog.user?.role}
              className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))]"
            >
              Update Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformAdmin;
